/**
 * `announcement_bar` — one time-sensitive line above everything (spec 42 R2,
 * R10; design 43 SF1).
 *
 * Placement is not the merchant's to choose: R10 pins the bar to the top of
 * the body whatever position it holds in the section list. A "banner" three
 * sections down is not a banner.
 *
 * Dismissal is remembered in `localStorage`, keyed by store slug **and a hash
 * of the text** — so editing the message re-shows it to someone who dismissed
 * the previous one, which is the behaviour a merchant expects when they change
 * the announcement (spec 42 Q3 recommendation).
 */
import { useEffect, useState } from "react";
import type { LinkTarget } from "@flint/api-contract/store-home";
import { resolveTarget, isExternal, type TargetContext } from "./resolve-target.js";

/** Small, stable, non-cryptographic — this only has to change when the text does. */
function textKey(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

const TONES = {
  info: { bg: "var(--info-50)", fg: "var(--ink)" },
  promo: { bg: "var(--accent-subtle)", fg: "var(--accent)" },
} as const;

/**
 * Per-section colour, independent of the theme (module 22 D4). Unlike other
 * sections, the announcement bar is full-bleed and self-colours (the section
 * band wrapper skips it), so it reads the scheme here. `default` keeps the
 * tone-based look; the rest are a strong, section-only override.
 */
const SCHEMES = {
  muted: { bg: "var(--paper)", fg: "var(--ink)" },
  accent: { bg: "var(--accent)", fg: "var(--accent-fg)" },
  dark: { bg: "var(--ink)", fg: "#f5f5f4" },
} as const;

export function AnnouncementBar({
  text,
  tone,
  scheme,
  dismissible,
  target,
  ctx,
}: {
  text: string;
  tone: "info" | "promo";
  /** Section-only colour override; `default`/absent → the tone look. */
  scheme?: "default" | "muted" | "accent" | "dark" | undefined;
  dismissible: boolean;
  target?: LinkTarget | undefined;
  ctx: TargetContext;
}) {
  const storageKey = `flint-ann-${ctx.storeSlug}-${textKey(text)}`;
  const [dismissed, setDismissed] = useState(false);

  // Read after mount, never during render: the server has no localStorage, and
  // a mismatch here would be a hydration error on every page.
  useEffect(() => {
    try {
      if (window.localStorage.getItem(storageKey) === "1") setDismissed(true);
    } catch {
      // Private mode / storage disabled — the bar simply keeps showing.
    }
  }, [storageKey]);

  if (dismissed) return null;

  const href = target ? resolveTarget(target, ctx) : null;
  // Section-only colour wins over the tone-derived (theme) look; `default` and
  // absent fall back to the tone so existing bars are untouched.
  const palette = scheme && scheme !== "default" ? SCHEMES[scheme] : TONES[tone];

  const label = href ? (
    <a
      href={href}
      {...(target && isExternal(target) ? { rel: "noopener noreferrer", target: "_blank" } : {})}
      style={{ color: "inherit", textDecoration: "underline" }}
    >
      {text}
    </a>
  ) : (
    text
  );

  return (
    <div
      role="region"
      aria-label="Store announcement"
      className="flint-announce"
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    >
      <span className="flint-announce-text">{label}</span>
      {dismissible ? (
        <button
          type="button"
          aria-label="Dismiss announcement"
          onClick={() => {
            setDismissed(true);
            try {
              window.localStorage.setItem(storageKey, "1");
            } catch {
              // Non-persistent dismissal is still better than none.
            }
          }}
          className="flint-announce-close"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
